// [목적] 댓글 섹션 — 목록(더 보기) + 작성 폼 + 본인 댓글 수정/삭제 + 신고. 폴리오·축제 공용
// [이유] fe-folio-social-wire — FolioPage(데스크톱)·FolioMobile(댓글 탭) 공용 컴포넌트로 분리해
//         두 화면이 동일 로직/스타일을 공유(FolioPage.tsx 의 기존 CongestionForecastCard 분리 관행과 동일).
//         [수정: 2026-09-21] festival-comments Spec D-8 — 축제 상세도 같은 UI 를 쓰도록 folioId 하드결합을
//         "댓글 대상 어댑터(CommentTargetAdapter)"로 일반화. 마크업·스타일·카피는 변경 없음(diff 0).
//         호출부는 얇은 래퍼 FolioCommentSection / FestivalCommentSection 을 사용한다.
// [사이드 이팩트] useComments(adapter) 훅으로 목록/CRUD 수행. 게스트 작성 시도는
//                 useAuthGate(adapter.commentIntent) 로 가로채 LoginPromptModal 노출.
//                 FlowLine A7(054b57db 일부): 비작성자 댓글에 "신고" 버튼 추가(ReportDialog 재사용,
//                 useAuthGate('report.create') 게스트 가드). 금칙어(content.BANNED_WORD_DETECTED) 작성/수정
//                 실패 시 인라인 안내 문구 표시.
// [수정 시 고려사항] 대댓글(parentId) 입력 UI 는 범위 밖 — create() 는 항상 최상위 댓글로 전송(ponytail).
//                   삭제는 window.confirm 네이티브 확인 다이얼로그 사용(별도 모달 컴포넌트 생략, ponytail).

import { useState } from 'react';
import { Box, ButtonBase, CircularProgress, InputBase, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';

import { ApiError } from '../../api/http.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useAuthGate } from '../../hooks/useAuthGate.js';
import { festivalCommentAdapter } from '../../hooks/useFestivalComments.js';
import { folioCommentAdapter } from '../../hooks/useFolioComments.js';
import { useComments, type CommentTargetAdapter } from '../../hooks/useComments.js';
import { LoginPromptModal } from '../auth/LoginPromptModal.js';
import { ReportDialog } from '../report/ReportDialog.js';

/**
 * [목적] ApiError 가 금칙어 코드인지 판별
 * [이유] content.BANNED_WORD_DETECTED 는 댓글 작성/수정 양쪽에서 동일하게 안내해야 함.
 * [사이드 이팩트] 없음(순수 함수)
 * [수정 시 고려사항] apps/api core/exception/codes.ts 가 정본 코드 문자열.
 */
function isBannedWordError(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'content.BANNED_WORD_DETECTED';
}
const BANNED_WORD_MESSAGE = '금칙어가 포함되어 있어요. 표현을 수정해 주세요.';

/**
 * [목적] 댓글 작성 시각을 KST(Asia/Seoul) 기준 "YYYY-MM-DD HH:mm" 문자열로 포맷
 * [이유] QA-P2-05: 기존에는 ISO 문자열(UTC)을 slice(0,16)로 그대로 잘라 표시해
 *        사용자 로컬 시각(KST)과 9시간 차이가 나는 버그가 있었다.
 * [사이드 이팩트] 없음(순수 함수) — Intl.DateTimeFormat 은 브라우저 내장 API로 추가 의존성 없음.
 * [수정 시 고려사항] 다른 화면에도 동일한 시각 표시가 필요해지면 공통 유틸로 승격할 것.
 */
function formatKstDateTime(iso: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`;
}

export type { CommentTargetAdapter };

/**
 * [목적] CommentRow 가 실제로 읽는 댓글 필드만 뽑은 구조 타입 — 폴리오/축제 댓글 아이템 공통 최소 계약
 * [이유] TR-13 — FolioComment(portfolioId)·FestivalComment(festivalContentId)는 대상 키 필드만 다르고 행 렌더에
 *        필요한 7필드는 같다. 공유 타입 패키지에 추가하지 않고 소비처(이 파일)에서 구조적으로 정의한다.
 * [사이드 이팩트] 없음(타입 선언만)
 * [수정 시 고려사항] CommentRow 가 새 필드를 읽게 되면 여기에 추가하고 두 댓글 스키마에 존재하는지 확인할 것.
 */
export interface GenericComment {
  id: string;
  authorUserId: string | null;
  body: string | null;
  hidden: boolean;
  isDeleted: boolean;
  createdAt: string;
  author?: { handle: string; displayName: string | null; avatarUrl: string | null } | null;
}

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const PAPER0 = c.paper[0][L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];

/**
 * [목적] 댓글 1건 표시 행 — 작성자/시각/본문 + 본인 댓글일 때 수정/삭제
 * [이유] fe-folio-social-wire — soft-delete tombstone(body=null, isDeleted=true) 는 "삭제된 댓글" 텍스트로 표시.
 * [사이드 이팩트] 수정 모드 진입 시 로컬 편집 텍스트 상태만 사용, 저장 시 patch() 호출.
 * [수정 시 고려사항] isMine 판정은 comment.authorUserId === 로그인 사용자 id 기준 — 탈퇴/익명 댓글은 항상 false.
 */
function CommentRow({
  comment,
  isMine,
  onPatch,
  onDelete,
  onReport,
}: {
  comment: GenericComment;
  isMine: boolean;
  onPatch: (text: string) => Promise<unknown>;
  onDelete: () => Promise<unknown>;
  onReport: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body ?? '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function handleSave(): Promise<void> {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onPatch(text);
      setEditing(false);
    } catch (err) {
      setSaveError(isBannedWordError(err) ? BANNED_WORD_MESSAGE : '댓글 수정에 실패했어요.');
    } finally {
      setSaving(false);
    }
  }

  function handleDeleteClick(): void {
    // ponytail: 별도 확인 모달 대신 네이티브 confirm 사용 — 삭제 빈도가 낮은 보조 액션
    if (window.confirm('댓글을 삭제할까요?')) {
      void onDelete();
    }
  }

  const authorLabel = comment.author?.displayName ?? comment.author?.handle ?? '알 수 없음';

  return (
    <Box sx={{ p: '12px 0', borderBottom: `1px solid ${BORDER}` }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', mb: '4px' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: INK_HI }}>
          {comment.isDeleted ? '(삭제됨)' : authorLabel}
        </Typography>
        <Typography
          sx={{
            fontSize: 10.5,
            color: INK_LOW,
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
          }}
        >
          {formatKstDateTime(comment.createdAt)}
        </Typography>
      </Box>

      {comment.isDeleted ? (
        <Typography sx={{ fontSize: 12.5, color: INK_LOW, fontStyle: 'italic' }}>
          삭제된 댓글입니다.
        </Typography>
      ) : editing ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <InputBase
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            multiline
            inputProps={{ 'aria-label': '댓글 수정' }}
            sx={{
              fontSize: 12.5,
              color: INK_HI,
              border: `1px solid ${BORDER}`,
              borderRadius: `${tokens.radius.s}px`,
              p: '8px 10px',
            }}
          />
          <Box sx={{ display: 'flex', gap: '6px' }}>
            <ButtonBase
              onClick={handleSave}
              disabled={saving || !draft.trim()}
              aria-label="댓글 수정 저장"
              sx={{
                // [수정: 디자인 개편] 비활성을 opacity가 아닌 배경/글자색으로 명확히 표시
                bgcolor: saving || !draft.trim() ? PAPER2 : BRAND,
                color: saving || !draft.trim() ? INK_LOW : PAPER0,
                borderRadius: `${tokens.radius.s}px`,
                px: '10px',
                py: '5px',
                fontSize: 11,
                fontWeight: 700,
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              저장
            </ButtonBase>
            <ButtonBase
              onClick={() => {
                setEditing(false);
                setDraft(comment.body ?? '');
              }}
              aria-label="댓글 수정 취소"
              sx={{
                color: INK_MID,
                border: `1px solid ${BORDER}`,
                borderRadius: `${tokens.radius.s}px`,
                px: '10px',
                py: '5px',
                fontSize: 11,
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              취소
            </ButtonBase>
          </Box>
          {saveError && (
            <Typography role="alert" sx={{ fontSize: 11, color: c.danger[500][L] }}>
              {saveError}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography
          sx={{ fontSize: 12.5, color: INK_HI, lineHeight: 1.55, whiteSpace: 'pre-line' }}
        >
          {comment.body}
        </Typography>
      )}

      {isMine && !comment.isDeleted && !editing && (
        <Box sx={{ display: 'flex', gap: '10px', mt: '6px' }}>
          <ButtonBase
            onClick={() => setEditing(true)}
            aria-label="댓글 수정"
            sx={{
              fontSize: 11,
              color: INK_MID,
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            수정
          </ButtonBase>
          <ButtonBase
            onClick={handleDeleteClick}
            aria-label="댓글 삭제"
            sx={{
              fontSize: 11,
              color: c.danger[500][L],
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            삭제
          </ButtonBase>
        </Box>
      )}

      {/* FlowLine A7: 비작성자 댓글 신고 버튼 — 삭제된 댓글은 신고 대상에서 제외 */}
      {!isMine && !comment.isDeleted && (
        <Box sx={{ display: 'flex', gap: '10px', mt: '6px' }}>
          <ButtonBase
            onClick={onReport}
            aria-label="댓글 신고"
            sx={{
              fontSize: 11,
              color: INK_LOW,
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            신고
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
}

/**
 * [목적] 댓글 섹션 진입점 — 목록 + 더 보기 + 작성 폼 (댓글 대상은 어댑터로 주입)
 * [이유] fe-folio-social-wire — FolioPage/FolioMobile 공용. festival-comments Spec D-8 로 축제 상세도 공유.
 * [사이드 이팩트] 작성 시도 시 guest 면 useAuthGate 가 로그인 모달을 띄우고 실제 create() 호출은 건너뜀.
 *                 useAuthGate 는 훅 생성 시 intent 고정 관례 — adapter.commentIntent 는 마운트 동안 바뀌지 않는다고 가정.
 * [수정 시 고려사항] 대상 id(folioId/contentId)는 부모가 로딩 완료 후에만 렌더한다고 가정(항상 truthy).
 *                   직접 사용보다 FolioCommentSection / FestivalCommentSection 래퍼 사용을 권장.
 */
export function CommentSection({
  adapter,
}: {
  adapter: CommentTargetAdapter<GenericComment>;
}): JSX.Element {
  const { user } = useAuth();
  const { guard, isPromptOpen, closePrompt, pendingIntent } = useAuthGate(adapter.commentIntent);
  // FlowLine A7: 댓글 신고 게스트 가드 — 별도 인스턴스(AD-4, useAuthGate.ts 관례)
  const reportGate = useAuthGate('report.create');
  const {
    items,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    create,
    isCreating,
    patch,
    remove,
  } = useComments(adapter);

  const [text, setText] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  // FlowLine A7: 신고 대상 댓글 id — null 이면 ReportDialog 미표시
  const [reportCommentId, setReportCommentId] = useState<string | null>(null);

  function handleSubmit(): void {
    const t = text.trim();
    if (!t) return;
    guard(async () => {
      setFormError(null);
      try {
        await create(t);
        setText('');
      } catch (err) {
        setFormError(isBannedWordError(err) ? BANNED_WORD_MESSAGE : '댓글 등록에 실패했어요.');
      }
    });
  }

  function handleReportComment(commentId: string): void {
    reportGate.guard(() => setReportCommentId(commentId));
  }

  return (
    <Box component="section" aria-label="댓글" sx={{ mt: `${tokens.spacing[4]}px` }}>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: INK_HI, mb: '12px' }}>
        댓글 {items.length > 0 ? items.length : ''}
      </Typography>

      {/* 작성 폼
          [수정: 디자인 개편 2026-09-20] 입력창과 "등록"이 각각 테두리를 가진 별개 요소로 나란히 놓여
          하나의 입력 영역으로 읽히지 않았고, 멀티라인이라 줄이 늘면 버튼이 위로 늘어나 보였다.
          입력창+버튼을 하나의 필드 컨테이너로 묶고 focus-within 링을 주어 포컬스 상태를 명시한다. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '6px',
          mb: '14px',
          bgcolor: PAPER0,
          border: `1px solid ${BORDER}`,
          borderRadius: `${tokens.radius.m}px`,
          p: '10px 12px',
          transition: `border-color ${tokens.motion.fast.duration}ms, box-shadow ${tokens.motion.fast.duration}ms`,
          '&:focus-within': {
            borderColor: `${BRAND}80`,
            boxShadow: `0 0 0 3px ${BRAND}1F`,
          },
        }}
      >
        <InputBase
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder="댓글을 남겨보세요"
          inputProps={{ 'aria-label': '댓글 입력' }}
          multiline
          sx={{
            width: '100%',
            fontSize: 12.5,
            lineHeight: 1.6,
            color: INK_HI,
            // 테두리는 부모 컨테이너가 담당 — 이중 보더 제거
            border: 'none',
            p: 0,
          }}
        />
        <ButtonBase
          onClick={handleSubmit}
          disabled={!text.trim() || isCreating}
          aria-label="댓글 등록"
          sx={{
            // [수정: 디자인 개편] 배경만 회색으로 바뀌고 글자색은 여전히 흰색이라 비활성이 덜 드러났다 — 글자색도 함께 전환
            bgcolor: text.trim() && !isCreating ? BRAND : PAPER2,
            color: text.trim() && !isCreating ? PAPER0 : INK_LOW,
            borderRadius: `${tokens.radius.s}px`,
            px: '16px',
            height: 30,
            fontSize: 11.5,
            fontWeight: 700,
            flexShrink: 0,
            '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
          }}
        >
          {isCreating ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : '등록'}
        </ButtonBase>
      </Box>

      {formError && (
        <Typography role="alert" sx={{ fontSize: 11, color: c.danger[500][L], mb: '10px' }}>
          {formError}
        </Typography>
      )}

      {Boolean(error) && !isLoading && (
        <Box role="alert" sx={{ fontSize: 12, color: INK_MID, py: '12px', textAlign: 'center' }}>
          댓글을 불러오지 못했습니다.
        </Box>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: '20px' }}>
          <CircularProgress size={20} aria-label="댓글 불러오는 중" />
        </Box>
      )}

      {!isLoading && !error && items.length === 0 && (
        /* [수정: 디자인 개편 2026-09-20] 빈 상태가 회색 한 줄로만 떠 있어 로딩 실패처럼 보였다 —
           점선 프레임을 씨워 "비어있는 영역"임을 분명히 한다. */
        <Box
          sx={{
            textAlign: 'center',
            py: '26px',
            color: INK_LOW,
            border: `1px dashed ${BORDER}`,
            borderRadius: `${tokens.radius.m}px`,
            bgcolor: PAPER1,
          }}
        >
          <Typography sx={{ fontSize: 12, color: INK_MID }}>아직 댓글이 없습니다.</Typography>
        </Box>
      )}

      {!isLoading &&
        items.map((cm) => (
          <CommentRow
            key={cm.id}
            comment={cm}
            isMine={Boolean(user) && cm.authorUserId === user?.id}
            onPatch={(t) => patch(cm.id, t)}
            onDelete={() => remove(cm.id)}
            onReport={() => handleReportComment(cm.id)}
          />
        ))}

      {hasNextPage && (
        <Box sx={{ textAlign: 'center', pt: '10px' }}>
          <ButtonBase
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            aria-label="댓글 더 보기"
            sx={{
              px: '18px',
              py: '7px',
              fontSize: 12,
              color: BRAND,
              border: `1px solid ${BORDER}`,
              borderRadius: `${tokens.radius.m}px`,
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            {isFetchingNextPage ? <CircularProgress size={14} color="inherit" /> : '더 보기'}
          </ButtonBase>
        </Box>
      )}

      <LoginPromptModal open={isPromptOpen} intent={pendingIntent} onClose={closePrompt} />
      <LoginPromptModal
        open={reportGate.isPromptOpen}
        intent={reportGate.pendingIntent}
        onClose={reportGate.closePrompt}
      />
      {reportCommentId && (
        <ReportDialog
          targetType={adapter.reportTargetType}
          targetId={reportCommentId}
          open
          onClose={() => setReportCommentId(null)}
        />
      )}
    </Box>
  );
}

/**
 * [목적] 폴리오 댓글 섹션 래퍼 — folioId 만 받아 폴리오 어댑터로 CommentSection 렌더
 * [이유] festival-comments Spec D-8 — FolioPage/FolioMobile 호출부 변경을 import·태그명 1줄로 축소(회귀 위험 최소화).
 * [사이드 이팩트] 폴리오 요청 경로·queryKey(['folioSocial','comments',folioId,...])는 리팩터 전과 동일.
 * [수정 시 고려사항] folioId 는 부모가 폴리오 로딩 완료 후에만 렌더한다고 가정(항상 truthy).
 */
export function FolioCommentSection({ folioId }: { folioId: string }): JSX.Element {
  return <CommentSection adapter={folioCommentAdapter(folioId)} />;
}

/**
 * [목적] 축제 댓글 섹션 래퍼 — TourAPI contentId 만 받아 축제 어댑터로 CommentSection 렌더
 * [이유] festival-comments Spec D-8 — 축제 상세(/festivals/:id) 하단 댓글. 폴리오와 동일 UI/동작.
 * [사이드 이팩트] 마운트 즉시 GET /festivals/:id/comments 1회 호출 — 부모가 축제 로딩 성공 분기에서만 렌더해야 한다
 *                 (로딩/404/오류에서는 요청 0회 계약).
 * [수정 시 고려사항] 대댓글 입력 UI 는 없음(폴리오 현행과 동일, Spec OQ-1).
 */
export function FestivalCommentSection({
  festivalContentId,
}: {
  festivalContentId: string;
}): JSX.Element {
  return <CommentSection adapter={festivalCommentAdapter(festivalContentId)} />;
}
